import {
  Box,
  Grid,
  Toolbar,
  Typography,
  TextField,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  Pagination,
  Tooltip,
  IconButton,
  Button,
  Modal,
  Menu,
  Card,
  CardContent,
  Stack,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import InputAdornment from "@mui/material/InputAdornment";
import SearchRounded from "@mui/icons-material/SearchRounded";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import axios from "axios";
import { API } from "../../../utils/Api";
import { toast } from "react-toastify";
import { CloudDownloadOutlined, InfoOutlined } from "@mui/icons-material";
import { jsPDF } from "jspdf";
import "jspdf-autotable";

function Inventory() {
  const token = localStorage.getItem("token");

  const [rawMaterials, setRawMaterials] = useState([]);
  const [loading, setLoading] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [outletData] = useState(JSON.parse(localStorage.getItem("outletData")));
  const [selectedCategory, setSelectedCategory] = useState("");
  const [categories, setCategories] = useState([]);

  const [stockFilter, setStockFilter] = useState("");

  const [actionQuantities, setActionQuantities] = useState({});

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuMaterial, setMenuMaterial] = useState(null);
  const menuOpen = Boolean(menuAnchorEl);

  const [modalOpen, setModalOpen] = useState(false);
  const [storageLocations, setStorageLocations] = useState([]);
  const [cityStorageLocations, setCityStorageLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [cities, setCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState("");
  const [currentTransferMaterial, setCurrentTransferMaterial] = useState(null);
  const [actionType, setActionType] = useState("");

  const [confirmUpdateOpen, setConfirmUpdateOpen] = useState(false);
  const [currentUpdateMaterial, setCurrentUpdateMaterial] = useState(null);
  const capitalizeWords = (str = "") => str.replace(/\b\w/g, (char) => char.toUpperCase());
  const normalize = (v) => String(v ?? "").toLowerCase().trim();

  const getCurrentDate = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getRawMaterialsList = async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedCategory) params.mainCategory = selectedCategory;

      const response = await axios.get(
        `${API.BASE_URL}stock-transactions/getOutletCurrentStock?outletId=${outletData._id}&limit=100000`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { ...params },
        }
      );

      const totalRecords = response?.data?.data?.totalRecords;
      setCategories(response?.data?.data?.rawMaterialCategories);

      if (
        typeof totalRecords === "string" &&
        totalRecords.includes("No Records")
      ) {
        setRawMaterials([]);
      } else {
        setRawMaterials(totalRecords || []);
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Failed to fetch raw materials", { autoClose: 3000 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getRawMaterialsList();
  }, [selectedCategory]);

  const generatePDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    const title = capitalizeWords("raw material inventory");
    const textWidth = doc.getTextWidth(title);
    const titleX = (pageWidth - textWidth) / 2;
    doc.text(title, titleX, 20);

    doc.setFontSize(10);

    const outletName = capitalizeWords(outletData?.name);
    const outletAddress = capitalizeWords(
      outletData?.address || "No Address Available"
    );
    const outletContact = `${outletData?.email || "No Email"}, ${outletData?.phone || "No Phone Info"
      }`;

    doc.text(`Outlet: ${outletName}`, 14, 30);
    doc.text(`Address: ${outletAddress}`, 14, 40);
    doc.text(`Contact: ${outletContact}`, 14, 50);

    doc.setFontSize(12);
    const startY = 60;

    const columns = [
      { header: capitalizeWords("#"), dataKey: "itemNo" },
      { header: capitalizeWords("product name"), dataKey: "productName" },
      { header: capitalizeWords("category"), dataKey: "category" },
      { header: capitalizeWords("sub category"), dataKey: "subcategory" },
      { header: capitalizeWords("stock quantity"), dataKey: "stockQuantity" },
      { header: capitalizeWords("unit"), dataKey: "unit" },
    ];

    const sortedMaterials = [...rawMaterials].sort((a, b) => {
      const categoryA =
        a?.rawMaterial?.mainCategory?.name?.toLowerCase?.() || "";
      const categoryB =
        b?.rawMaterial?.mainCategory?.name?.toLowerCase?.() || "";
      const subcategoryA =
        a?.rawMaterial?.subCategory?.name?.toLowerCase?.() || "";
      const subcategoryB =
        b?.rawMaterial?.subCategory?.name?.toLowerCase?.() || "";

      if (categoryA < categoryB) return -1;
      if (categoryA > categoryB) return 1;
      if (subcategoryA < subcategoryB) return -1;
      if (subcategoryA > subcategoryB) return 1;
      return 0;
    });

    const rows = sortedMaterials.map((material, index) => ({
      itemNo: index + 1,
      productName: capitalizeWords(material?.rawMaterial?.name || ""),
      category: capitalizeWords(material?.rawMaterial?.mainCategory?.name || ""),
      subcategory: capitalizeWords(material?.rawMaterial?.subCategory?.name || ""),
      stockQuantity: parseFloat((material?.currentStock ?? 0).toFixed(2)),
      unit: capitalizeWords(material?.rawMaterial?.unit || ""),
    }));

    doc.autoTable({
      columns,
      body: rows,
      startY,
      theme: "grid",
      styles: { halign: "center" },
      columnStyles: {
        0: { cellWidth: 10 },
        1: { cellWidth: 60 },
        2: { cellWidth: 30 },
        3: { cellWidth: 30 },
        4: { cellWidth: 30 },
        5: { cellWidth: 20 },
      },
    });

    doc.save(`${getCurrentDate()}-Raw-Material-Inventory.pdf`);
  };

  const handleCategoryChange = (event) => {
    setSelectedCategory(event.target.value);
    setCurrentPage(1);
  };

  const uniqueCategories = useMemo(() => {
    return categories?.filter(
      (category, index, self) =>
        index ===
        self.findIndex(
          (c) => c?.mainCategory?._id === category?.mainCategory?._id
        )
    );
  }, [categories]);

  const filteredMaterials = useMemo(() => {
    const list = Array.isArray(rawMaterials) ? rawMaterials : [];

    const q = normalize(searchQuery);
    const words = q ? q.split(/\s+/).filter(Boolean) : [];

    return list.filter((m) => {
      const isOut = m?.stockInfo === "Out of Stock";
      if (stockFilter === "OUT_OF_STOCK" && !isOut) return false;
      if (stockFilter === "IN_STOCK" && isOut) return false;

      if (selectedCategory) {
        const catId = m?.rawMaterial?.mainCategory?._id;
        if (catId !== selectedCategory) return false;
      }

      if (!words.length) return true;

      const haystack = normalize([
        m?.rawMaterial?.name,
        m?.rawMaterial?.mainCategory?.name,
        m?.rawMaterial?.subCategory?.name,
        m?.rawMaterial?.unit,
        m?.stockInfo,
        m?.currentStock,
      ].join(" "));

      return words.every((w) => haystack.includes(w));
    });
  }, [rawMaterials, searchQuery, stockFilter, selectedCategory]);

  const pageOffset = (currentPage - 1) * itemsPerPage;

  const paginatedMaterials = filteredMaterials.slice(
    pageOffset,
    currentPage * itemsPerPage
  );

  const handlePageChange = (event, page) => setCurrentPage(page);

  const handleActionQuantityChange = (id, quantity) => {
    setActionQuantities((prev) => ({ ...prev, [id]: quantity }));
  };

  const openMenu = (e, material) => {
    setMenuAnchorEl(e.currentTarget);
    setMenuMaterial(material);
  };

  const closeMenu = () => {
    setMenuAnchorEl(null);
    setMenuMaterial(null);
  };

  const handleMenuAction = (type) => {
    closeMenu();
    setCurrentTransferMaterial(menuMaterial);
    setActionType(type);
    setModalOpen(true);
    setSelectedCity("");
    setSelectedLocation("");
  };

  const getStorageLocation = async () => {
    try {
      const response = await axios.get(`${API.BASE_URL}storage-location`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const locations = response?.data?.data?.storageLocations || [];

      const cityGroups = locations.reduce((acc, loc) => {
        if (!acc[loc.city]) acc[loc.city] = [];
        acc[loc.city].push(loc);
        return acc;
      }, {});
      setStorageLocations(locations);
      setCities(Object.keys(cityGroups));
    } catch (error) {
      if (error.response && error.response?.data?.status === false) {
        toast.error(error.response.data.error, {
          position: "top-right",
          autoClose: 3000,
          theme: "colored",
        });
      } else {
        toast.error("Failed to fetch storage locations", { autoClose: 3000 });
      }
    }
  };

  useEffect(() => {
    getStorageLocation();
  }, []);

  const getStorageLocationCurrentStock = async () => {
    try {
      const response = await axios.get(
        `${API.BASE_URL}storage-location/getCurrentStock`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            city: selectedCity,
            rawMaterialId: currentTransferMaterial?.rawMaterial?._id,
          },
        }
      );
      setCityStorageLocations(response?.data?.data?.storageLocations || []);
    } catch (error) {
      if (error.response && error.response?.data?.status === false) {
        toast.error(error.response.data.error, {
          position: "top-right",
          autoClose: 3000,
          theme: "colored",
        });
      } else {
        toast.error("Failed to fetch location stock", { autoClose: 3000 });
      }
    }
  };

  useEffect(() => {
    if (actionType === "add" && selectedCity) {
      getStorageLocationCurrentStock();
    }
  }, [selectedCity, actionType]);

  const handleCityChange = (e) => {
    const city = e.target.value;
    setSelectedCity(city);
    setCityStorageLocations(storageLocations.filter((loc) => loc.city === city));
    setSelectedLocation("");
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedLocation("");
    setSelectedCity("");
  };

  const getRawMaterialStockCentral = async (rawMaterialId) => {
    try {
      const response = await axios.get(
        `${API.BASE_URL}stock-transactions/getCurrentStock?rawMaterialId=${rawMaterialId}&storageLocation=${selectedLocation}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response?.data;
    } catch (error) {
      console.error("Error:", error?.response?.data);
      let errorMessage =
        error?.response?.data?.data?.message ||
        error?.response?.data?.error ||
        "Failed to fetch stock";
      toast.error(errorMessage, { autoClose: 3000 });
    }
  };

  const handleDebitStockOutletToCentral = async (
    rawMaterialId,
    currentStockQuantity
  ) => {
    const transferQuantity = actionQuantities[rawMaterialId];

    try {
      const response = await axios.post(
        `${API.BASE_URL}stock-transactions/debit-stock-from-outlet-to-central`,
        {
          outletId: outletData._id,
          outletStockTransferData: [
            {
              rawMaterialId,
              currentStockQuantity,
              transferQuantity,
            },
          ],
          storageLocation: selectedLocation,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response?.data?.status === true) {
        toast.success("Central Stock updated successful!", { autoClose: 3000 });
        getRawMaterialsList();
        setActionQuantities((prev) => ({ ...prev, [rawMaterialId]: "" }));
      }
    } catch (error) {
      console.error("Error:", error?.response?.data);
      let errorMessage =
        error?.response?.data?.data?.message ||
        error?.response?.data?.error ||
        "Transfer failed";
      toast.error(errorMessage, { autoClose: 3000 });
    }
  };

  const handleAddStockCentralToOutlet = async (rawMaterialId) => {
    try {
      const response = await getRawMaterialStockCentral(rawMaterialId);
      const currentStockQuantity = response?.data?.currentStock;

      if (currentStockQuantity === null || currentStockQuantity === undefined) {
        toast.error("Unable to fetch current stock.", { autoClose: 3000 });
        return;
      }

      const transferQuantity = actionQuantities[rawMaterialId];

      const addStockResponse = await axios.post(
        `${API.BASE_URL}stock-transactions/addStockToOutlet`,
        {
          outletId: outletData._id,
          outletStockTransferData: [
            {
              rawMaterialId,
              currentStockQuantity,
              transferQuantity,
              storageLocation: selectedLocation,
            },
          ],
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (addStockResponse?.data?.status === true) {
        toast.success("Outlet Stock updated successfully!", { autoClose: 3000 });
        getRawMaterialsList();
        setActionQuantities((prev) => ({ ...prev, [rawMaterialId]: "" }));
      }
    } catch (error) {
      console.error("Error:", error?.response?.data);
      let errorMessage =
        error?.response?.data?.data?.message ||
        error?.response?.data?.error ||
        "Stock update failed";
      toast.error(errorMessage, { autoClose: 3000 });
    }
  };

  const handleDebitStock = async (rawMaterialId, currentStockQuantity) => {
    try {
      const transferQuantity = actionQuantities[rawMaterialId];

      const response = await axios.post(
        `${API.BASE_URL}stock-transactions/debitStock`,
        {
          outletId: outletData._id,
          outletStockTransferData: [
            {
              rawMaterialId,
              currentStockQuantity,
              transferQuantity,
            },
          ],
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response?.data?.status === true) {
        toast.success("Stock updated successful!", { autoClose: 3000 });
        getRawMaterialsList();
        setActionQuantities((prev) => ({ ...prev, [rawMaterialId]: "" }));
      }
    } catch (error) {
      console.error("Error:", error?.response?.data);
      let errorMessage =
        error?.response?.data?.data?.message ||
        error?.response?.data?.error ||
        "Update failed";
      toast.error(errorMessage, { autoClose: 3000 });
    }
  };

  const handleModalConfirm = () => {
    if (!selectedLocation) {
      toast.error("Please select a storage location", { autoClose: 3000 });
      return;
    }

    const id = currentTransferMaterial?.rawMaterial?._id;
    const qty = actionQuantities[id];

    if (
      qty === "" ||
      qty === undefined ||
      qty === null ||
      Number.isNaN(Number(qty)) ||
      Number(qty) <= 0
    ) {
      toast.error("Please enter a valid quantity", { autoClose: 3000 });
      return;
    }

    if (actionType === "add") {
      handleAddStockCentralToOutlet(id);
    } else if (actionType === "debit") {
      handleDebitStockOutletToCentral(id, currentTransferMaterial?.currentStock);
    }

    handleModalClose();
  };

  const totals = useMemo(() => {
    const list = Array.isArray(rawMaterials) ? rawMaterials : [];
    const out = list.filter((m) => m?.stockInfo === "Out of Stock").length;
    return { total: list.length, out, in: Math.max(list.length - out, 0) };
  }, [rawMaterials]);

  const totalPages = Math.ceil((filteredMaterials?.length || 0) / itemsPerPage) || 1;
  
  return (
    <Box className="dashBoard" sx={{ background: "#f6f8fb", minHeight: "100vh" }}>
      <Toolbar />

      <Grid container justifyContent="center" sx={{ pb: 6 }}>
        <Grid item xs={12} md={11}>
          <Card
            elevation={0}
            sx={{
              mt: 4,
              borderRadius: 3,
              border: "1px solid #e6eaf0",
              background: "linear-gradient(135deg, #ffffff 0%, #f8fbff 100%)",
            }}
          >
            <CardContent sx={{ py: 3 }}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                alignItems={{ xs: "flex-start", md: "center" }}
                justifyContent="space-between"
                spacing={2}
              >
                <Box>
                  <Typography sx={{ fontSize: 20, fontWeight: 800, color: "#0f172a" }}>
                    Raw Materials Inventory
                  </Typography>
                  <Typography sx={{ mt: 0.5, fontSize: 13, color: "#64748b" }}>
                    Search, filter, update in-stock quantity, or use ⋮ for Get / Transfer.
                  </Typography>
                </Box>

                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
                  <Chip
                    label={`Total: ${totals.total}`}
                    variant="outlined"
                    sx={{ fontWeight: 800, borderRadius: 2 }}
                  />
                  <Chip
                    label={`In Stock: ${totals.in}`}
                    variant="outlined"
                    sx={{ fontWeight: 800, borderRadius: 2 }}
                  />
                  <Chip
                    label={`Out of Stock: ${totals.out}`}
                    variant="outlined"
                    sx={{
                      fontWeight: 900,
                      borderRadius: 2,
                      borderColor: "#fecaca",
                      color: "#991b1b",
                      background: "#fff1f2",
                    }}
                  />

                  <CloudDownloadOutlined
                    onClick={generatePDF}
                    sx={{ fontSize: 34, cursor: "pointer", color: "#003049" }}
                  />
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          <Card
            elevation={0}
            sx={{
              mt: 2,
              borderRadius: 3,
              border: "1px solid #e6eaf0",
              background: "#fff",
            }}
          >
            <CardContent sx={{ py: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={5}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#334155", mb: 0.8 }}>
                    Search Raw Materials
                  </Typography>

                  <TextField
                    fullWidth
                    placeholder="Search by raw material name"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(String(e.target.value));
                      setCurrentPage(1);
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        height: 52,
                        background: "#fff",
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchRounded />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#334155", mb: 0.8 }}>
                    Category
                  </Typography>

                  <FormControl fullWidth sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, height: 52 } }}>
                    <Select value={selectedCategory} onChange={handleCategoryChange} displayEmpty>
                      <MenuItem value="">
                        <em>All</em>
                      </MenuItem>
                      {uniqueCategories?.map((category) => (
                        <MenuItem key={category?.mainCategory?._id} value={category?.mainCategory?._id}>
                          {category?.mainCategory?.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={3}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#334155", mb: 0.8 }}>
                    Stock Status
                  </Typography>

                  <FormControl fullWidth sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, height: 52 } }}>
                    <Select
                      value={stockFilter}
                      onChange={(e) => {
                        setStockFilter(e.target.value);
                        setCurrentPage(1);
                      }}
                      displayEmpty
                    >
                      <MenuItem value="">
                        <em>All</em>
                      </MenuItem>
                      <MenuItem value="IN_STOCK">In Stock</MenuItem>
                      <MenuItem value="OUT_OF_STOCK">Out of Stock</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid sx={{ display: "flex", alignContent: 'center', alignItems: 'center', justifyContent: "center" }} item xs={12} md={1}>
                  <Box sx={{}}>
                    <Tooltip
                      title="Enter the new In-Stock quantity and click Update. Use ⋮ for Get Stock / Transfer to Storage."
                      arrow
                    >
                      <IconButton>
                        <InfoOutlined />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Content */}
          <Card
            elevation={0}
            sx={{
              mt: 2,
              borderRadius: 3,
              border: "1px solid #e6eaf0",
              overflow: "hidden",
              background: "#fff",
            }}
          >
            <CardContent sx={{ p: 0 }}>
              {loading ? (
                <Box sx={{ py: 6, display: "flex", alignItems: "center", justifyContent: "center", gap: 2 }}>
                  <CircularProgress />
                  <Typography sx={{ color: "#334155", fontWeight: 700 }}>
                    Fetching Raw Materials...
                  </Typography>
                </Box>
              ) : filteredMaterials?.length === 0 ? (
                <Box sx={{ py: 6, textAlign: "center" }}>
                  <Typography sx={{ fontSize: 16, fontWeight: 800, color: "#0f172a" }}>
                    No raw materials to display
                  </Typography>
                  <Typography sx={{ mt: 0.5, fontSize: 13, color: "#64748b" }}>
                    Try changing search/category/stock filters.
                  </Typography>
                </Box>
              ) : (
                <>
                  <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #e6eaf0", background: "#fff" }}>
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center" justifyContent="space-between">
                      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                        <Chip
                          label={`Showing: ${paginatedMaterials?.length || 0}`}
                          variant="outlined"
                          sx={{ fontWeight: 800, borderRadius: 2 }}
                        />
                        <Chip
                          label={`Page: ${currentPage}/${totalPages}`}
                          variant="outlined"
                          sx={{ fontWeight: 800, borderRadius: 2 }}
                        />
                      </Stack>

                      <Chip
                        label="Daily Update"
                        sx={{
                          fontWeight: 900,
                          borderRadius: 2,
                          backgroundColor: "#003049",
                          color: "#fff",
                          "& .MuiChip-label": { color: "#fff" },
                        }}
                      />
                    </Stack>
                  </Box>

                  <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 0, maxHeight: "70vh", overflow: "auto" }}>
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow>
                          {["No.", "Product", "In Stock", "Update Qty", "Actions"].map((h) => (
                            <TableCell
                              key={h}
                              sx={{
                                fontWeight: 900,
                                color: "#0f172a",
                                background: "#f1f5f9",
                                borderBottom: "1px solid #e2e8f0",
                                whiteSpace: "nowrap",
                                position: "sticky",
                                top: 0,
                                zIndex: 2,
                              }}
                              align={h === "Product" ? "left" : "center"}
                            >
                              {h}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {paginatedMaterials.map((material, index) => {
                          const displayIndex = pageOffset + index + 1;
                          const id = material?.rawMaterial?._id;

                          const enteredQty = actionQuantities[id];
                          const isInvalidQty =
                            enteredQty === "" ||
                            enteredQty === undefined ||
                            enteredQty === null ||
                            Number.isNaN(Number(enteredQty)) ||
                            Number(enteredQty) < 0;

                          const isOutOfStock = material?.stockInfo === "Out of Stock";

                          return (
                            <TableRow
                              key={id || index}
                              hover
                              sx={{
                                backgroundColor: isOutOfStock
                                  ? "#fff1f2"
                                  : index % 2 === 0
                                    ? "#ffffff"
                                    : "#EDF9FF",
                                "& td": { borderBottom: "1px solid #f1f5f9" },
                              }}
                            >
                              <TableCell align="center" sx={{ fontWeight: 700, color: "#334155" }}>
                                {displayIndex}
                              </TableCell>

                              <TableCell align="left">
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Box>
                                    <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#0f172a", textTransform: "capitalize" }}>
                                      {material?.rawMaterial?.name || "N/A"}
                                    </Typography>

                                    <Typography sx={{ fontSize: 12, color: "#64748b", textTransform: "capitalize" }}>
                                      {material?.rawMaterial?.mainCategory?.name || "N/A"} •{" "}
                                      {material?.rawMaterial?.subCategory?.name || "N/A"}
                                    </Typography>

                                    <Typography sx={{ fontSize: 11, color: "#475569", mt: 0.3 }}>
                                      <b>Unit:</b> {material?.rawMaterial?.unit || "N/A"}
                                    </Typography>
                                  </Box>

                                  {isOutOfStock && (
                                    <Chip
                                      label="Out of Stock"
                                      size="small"
                                      sx={{
                                        background: "#ffe4e6",
                                        color: "#9f1239",
                                        fontWeight: 900,
                                        borderRadius: 2,
                                      }}
                                      variant="outlined"
                                    />
                                  )}
                                </Stack>
                              </TableCell>

                              <TableCell align="center" sx={{ fontWeight: 600 }}>
                                {Number(material?.currentStock || 0).toFixed(2)} {material?.rawMaterial?.unit}
                              </TableCell>

                              <TableCell align="center">
                                <TextField
                                  type="number"
                                  inputProps={{ min: 0, step: "0.01" }}
                                  value={actionQuantities[id] || ""}
                                  onChange={(e) => handleActionQuantityChange(id, e.target.value)}
                                  size="small"
                                  placeholder="Current In-Stock"
                                  sx={{
                                    width: 160,
                                    "& .MuiOutlinedInput-root": { borderRadius: 2, background: "#fff" },
                                  }}
                                />
                              </TableCell>

                              <TableCell align="center">
                                <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
                                  <Button
                                    onClick={() => {
                                      setCurrentUpdateMaterial(material);
                                      setConfirmUpdateOpen(true);
                                    }}
                                    disabled={isInvalidQty}
                                    variant="contained"
                                    sx={{
                                      height: 38,
                                      borderRadius: 2,
                                      fontWeight: 900,
                                      textTransform: "none",
                                      backgroundColor: "#003049",
                                      "&:hover": { backgroundColor: "#002437" },
                                      "&.Mui-disabled": { backgroundColor: "#b0bec5", color: "#ffffff" },
                                    }}
                                  >
                                    Update
                                  </Button>

                                  <Tooltip title="More actions (Get Stock / Transfer)">
                                    <span>
                                      <IconButton
                                        onClick={(e) => openMenu(e, material)}
                                        sx={{
                                          border: "1px solid #e6eaf0",
                                          borderRadius: 2,
                                          background: "#fff",
                                        }}
                                      >
                                        <MoreVertIcon />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Divider />

                  <Box sx={{ py: 2, display: "flex", justifyContent: "center" }}>
                    <Pagination
                      count={totalPages}
                      page={currentPage}
                      onChange={handlePageChange}
                      color="primary"
                      variant="outlined"
                      shape="rounded"
                    />
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* More actions menu */}
      <Menu anchorEl={menuAnchorEl} open={menuOpen} onClose={closeMenu}>
        <MenuItem onClick={() => handleMenuAction("add")}>Get Stock (from Storage)</MenuItem>
        <MenuItem onClick={() => handleMenuAction("debit")}>Transfer to Storage</MenuItem>
      </Menu>

      {/* Storage Location Modal (for Get Stock / Transfer) */}
      <Modal open={modalOpen} onClose={handleModalClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            width: { xs: "92%", sm: 720 },
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            background: "linear-gradient(135deg, #f7f9fc, #ffffff)",
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.2)",
            p: 4,
            borderRadius: 3,
          }}
        >
          <Typography variant="h5" sx={{ mb: 1, textAlign: "center", fontWeight: 900, color: "#003049" }}>
            Select Storage Location
          </Typography>

          <Typography variant="body2" sx={{ mb: 3, textAlign: "center", fontWeight: 600, color: "#334155" }}>
            {actionType === "debit"
              ? "The entered quantity will be transferred to the selected storage location from the outlet."
              : "The entered quantity will be debited from the selected storage location and transferred to the outlet."}
          </Typography>

          <Box sx={{ mb: 3 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 800, color: "#334155", mb: 0.8 }}>
              Quantity
            </Typography>
            <TextField
              fullWidth
              type="number"
              inputProps={{ min: 0, step: "0.01" }}
              variant="outlined"
              value={actionQuantities[currentTransferMaterial?.rawMaterial?._id] || ""}
              onChange={(e) =>
                handleActionQuantityChange(currentTransferMaterial?.rawMaterial?._id, e.target.value)
              }
              sx={{
                "& .MuiOutlinedInput-root": { borderRadius: 2, height: 52, background: "#fff" },
              }}
            />
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography sx={{ fontSize: 12, fontWeight: 800, color: "#334155", mb: 0.8 }}>
                City
              </Typography>
              <FormControl fullWidth>
                <Select
                  value={selectedCity}
                  onChange={handleCityChange}
                  displayEmpty
                  sx={{ bgcolor: "#fff", borderRadius: 2, height: 52 }}
                >
                  <MenuItem value="">
                    <em>Select a city</em>
                  </MenuItem>
                  {cities.map((city) => (
                    <MenuItem key={city} value={city}>
                      {city}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography sx={{ fontSize: 12, fontWeight: 800, color: "#334155", mb: 0.8 }}>
                Location
              </Typography>
              <FormControl fullWidth>
                <Select
                  value={selectedLocation}
                  onChange={(e) => setSelectedLocation(e.target.value)}
                  displayEmpty
                  disabled={!selectedCity}
                  sx={{ bgcolor: "#fff", borderRadius: 2, height: 52 }}
                >
                  <MenuItem value="">
                    <em>Select a location</em>
                  </MenuItem>

                  {cityStorageLocations?.map((location) => (
                    <MenuItem key={location?._id} value={location?._id} sx={{ display: "flex", justifyContent: "space-between" }}>
                      <span>{location?.name}</span>

                      {location?.stockInfo?.[0]?.currentStock !== undefined && (
                        <Chip
                          size="small"
                          label={`Stock: ${location.stockInfo[0].currentStock}`}
                          sx={{
                            ml: 1,
                            fontWeight: 900,
                            borderRadius: 2,
                            background: "#d1e7dd",
                            color: "#155724",
                          }}
                          variant="outlined"
                        />
                      )}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
            <Button
              variant="outlined"
              onClick={handleModalClose}
              sx={{
                flex: 1,
                height: 50,
                borderRadius: 2,
                fontWeight: 900,
                borderColor: "#d32f2f",
                color: "#d32f2f",
                "&:hover": { backgroundColor: "#fdecea", borderColor: "#c62828" },
              }}
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              onClick={handleModalConfirm}
              disabled={!selectedLocation}
              sx={{
                flex: 1,
                height: 50,
                borderRadius: 2,
                fontWeight: 900,
                backgroundColor: "#003049",
                "&:hover": { backgroundColor: "#002437" },
                "&.Mui-disabled": { backgroundColor: "#b0bec5", color: "#ffffff" },
              }}
            >
              Confirm
            </Button>
          </Stack>
        </Box>
      </Modal>

      {/* Confirm Update In-Stock */}
      <Modal open={confirmUpdateOpen} onClose={() => setConfirmUpdateOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "92%", sm: 460 },
            bgcolor: "background.paper",
            borderRadius: 3,
            boxShadow: 24,
            p: 4,
            textAlign: "center",
          }}
        >
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 900, color: "#003049" }}>
            Confirm Update Quantity
          </Typography>

          <Typography variant="body1" sx={{ mb: 3, color: "#334155" }}>
            Are you sure you want to update the In-Stock quantity of{" "}
            <strong style={{ textTransform: "capitalize" }}>{currentUpdateMaterial?.rawMaterial?.name}</strong> to{" "}
            <strong>{actionQuantities[currentUpdateMaterial?.rawMaterial?._id] ?? "N/A"}</strong>?
          </Typography>

          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              onClick={() => setConfirmUpdateOpen(false)}
              sx={{
                flex: 1,
                height: 48,
                borderColor: "#d32f2f",
                color: "#d32f2f",
                borderRadius: 2,
                fontWeight: 900,
                "&:hover": { backgroundColor: "#fdecea", borderColor: "#c62828" },
              }}
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              onClick={() => {
                handleDebitStock(
                  currentUpdateMaterial?.rawMaterial?._id,
                  currentUpdateMaterial?.currentStock
                );
                setConfirmUpdateOpen(false);
              }}
              sx={{
                flex: 1,
                height: 48,
                backgroundColor: "#003049",
                borderRadius: 2,
                fontWeight: 900,
                "&:hover": { backgroundColor: "#002437" },
              }}
            >
              Confirm
            </Button>
          </Stack>
        </Box>
      </Modal>
    </Box>
  );
}

export default Inventory;
